// react import
import Styles from './NodeParser.module.scss';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// component imports
import { AuthContext, ViewContext } from 'components/lib';
import { NodeTimeline } from 'components/global/NodeLayout/NodeTimeline';
import { NodeParserEditor } from 'components/global/NodeLayout/NodeParserEditor';
import InfoDrawer from 'components/global/NodeLayout/InfoDrawer';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import NodeProgress from 'components/global/NodeLayout/NodeProgress/NodeProgress';
import { Joyride } from 'components/walkthrough/walkthrough';
import { useAPI, Feedback } from 'components/lib';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';
import { LightTooltip } from 'components/global/NodeLayout/NodeTopostman';
import { HelperChat } from 'components/global/HelperChat/HelperChat';
import RevealAnswerKey from 'components/RevealAnswerKey/RevealAnswerKey';

// module imports
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { Avatar, Typography } from '@mui/material';
import { pink } from '@mui/material/colors';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import axios from 'axios';
import _ from 'lodash';

// asset imports
import failedAttemptSound from 'assets/sounds/try-again.mp3'
import { toposParser } from 'utils/helpers/parser';
import { decoder } from 'utils/helpers/decoder';
import { copyEnabledChecker } from 'utils/helpers/utility';
import { InstructionPane } from 'components/global/InstructionPane/InstructionPane';

const finalAudio = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/voice/finish_node.mp3`;

export function NodeParser(props) {
    const { lab_id, node_id } = useParams();
    const { resource } = useParams();
    const context = useContext(AuthContext);
    const isCopyEnabled = copyEnabledChecker(context?.user);
    const [maxUnlocked, setMaxUnlocked] = useState(0);
    const [nodeFinished, setNodeFinished] = useState(false);
    const [steps, setSteps] = useState();
    const [isMuted, setIsMuted] = useState(true);
    const [highlightText, setHighlightText] = useState('');
    const [userInput, setUserInput] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [nodeInfo, setNodeInfo] = useState();
    const [startLab, setStartLab] = useState(true);
    const [displayData, setDisplayData] = useState();
    const [copied, setCopied] = useState(false);

    const [showReveal, setShowReveal] = useState(false);

    const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`, 'GET');
    const navigate = useNavigate();
    const [showFinishConfirm, setShowFinishConfirm] = useState(false);
    const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);
    const audioRef = useRef(null);
    const finalAudioRef = useRef(null);
    const viewContext = useContext(ViewContext)


    useEffect(() => {
        if (data) {
            const jsonData = decoder(data);
            setNodeInfo(jsonData);
            setSteps(jsonData.steps.steps);
            jsonData.numOfSteps = jsonData.steps.steps.length;
            setDisplayData(jsonData.content.reference_data);
            document.title = props.t('Labs.Nodes.NodeParser.title', { nodeName: jsonData.name })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const wastedAttemptAudio = new Audio(failedAttemptSound);

    const handleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };


    const handleUserInputChange = (newValue) => {
        setUserInput(newValue);
    };

    function incrementMaxUnlocked() {
        if (maxUnlocked >= 0 && maxUnlocked < steps.length - 1) {
            setMaxUnlocked(maxUnlocked + 1);
            steps[currentStep].userInput = userInput
        } else {
            setNodeFinished(true)
        }
    }

    function moveToNext() {
        if (currentStep < maxUnlocked) {
            const nextStep = currentStep + 1;
            setCurrentStep(nextStep);
            setHighlightText('');

            if (nextStep === maxUnlocked) {
                setUserInput('');
            } else {
                setUserInput(steps[nextStep].userInput)
            }
        }
    }

    function moveToPrev() {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setUserInput(steps[currentStep - 1].userInput);
            setHighlightText('');
        }
    }

    function handleMatchClick(type) {
        const parseText =
            type === 'json'
                ? JSON.stringify(JSON.parse(steps[currentStep].text), null, 4)
                : type === 'xml'
                    ? nodeInfo.steps.text
                    : steps[currentStep].text;
        patternValidator(type, parseText, userInput);
    }

    function compareMatches(matchedText, expectedText) {
        return _.isEqual(matchedText, expectedText);
    }

    function patternValidator(type, textBody, userInput) {
        if (userInput?.length) {
            try {
                let matches = toposParser(type, textBody, userInput);
                let cleanedMatches;
                if (type === 'xml') {
                    cleanedMatches = matches.map((match) => {
                        if (typeof match === 'string') {
                            return match.replace(/[\n\t]/g, '');
                        }
                        return match;
                    });
                }

                const isMatch =
                    type === 'xml'
                        ? compareMatches(cleanedMatches, steps[currentStep].exp_match)
                        : compareMatches(matches, steps[currentStep].exp_match);
                setIsCorrect(isMatch);
                switch (type) {
                    case 'regex':
                        setHighlightText(userInput);
                        break;
                    case 'json':
                        setHighlightText(JSON.stringify(matches, null, 4));
                        break;
                    case 'xml':
                        setHighlightText(matches);
                        break;
                    default:
                        break;
                }
                if (isMatch && currentStep === maxUnlocked) {
                    incrementMaxUnlocked();
                }
            } catch (err) {
                wastedAttemptAudio.play()
                setHighlightText('');
                let text = ''
                switch (type) {
                    case 'regex':
                        text = props.t('Labs.Nodes.NodeParser.toast.regex')
                        break;
                    case 'json':
                        text = props.t('Labs.Nodes.NodeParser.toast.json')
                        break;
                    case 'xml':
                        text = props.t('Labs.Nodes.NodeParser.toast.xpath')
                        break;
                    default:
                        text = props.t('Labs.Nodes.NodeParser.toast.default')
                        break;
                }
                toast.dark(text, {
                    type: 'error',
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        } else {
            setHighlightText('');
            toast.dark(props.t('Labs.Nodes.NodeParser.toast.default'), {
                type: 'error',
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    }

    const handleVoiceClick = () => {
        setIsMuted(!isMuted);
    };


    const currentRef = useRef(null);


    useEffect(() => {
        if (!steps) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audioLink = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`;
        if (audioLink && !isMuted) {
            const newAudio = new Audio(audioLink);
            audioRef.current = newAudio;
            newAudio.play();
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isMuted]);

    useEffect(() => {
        if (!nodeFinished) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audio = new Audio(finalAudio);
        finalAudioRef.current = audio;
        audio.play();

        viewContext.notification.show(
            props.t('Labs.Nodes.NodeParser.end'),
            'info',
            false
        );

        return () => {
            if (finalAudioRef.current) {
                finalAudioRef.current.pause();
            }
        };

    }, [nodeFinished])

    // confirmation card cancel button
    const navigateBack = () => {
        navigate(-1);
    };

    // confirmation card confirm button
    const startLabClick = async () => {
        const options = {
            method: 'POST',
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
            headers: {
                accept: 'application/json',
            },
        };
        await axios.request(options)
            .then((response) => {
                new Promise((resolve) => setTimeout(resolve, 100));
                setStartLab(false);
                if (context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id) || nodeInfo.is_assessment === 1) {
                    setIsMuted(!isMuted)
                }
            }).catch((err) => {
                if (err.response.status === 403) {
                    navigate('/nopermission');
                } else {
                    navigate('/oops');
                }
            });
    };

    const handleNext = () => {
        const options = {
            method: "POST",
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
            headers: {
                accept: 'application/json',
            },
            ...(nodeInfo.is_assessment === 1 && { data: { completionPercent: roundedPercentage } }),
        };
        axios
            .request(options)
            .then(async function (response) {
                await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
                if (nodeInfo.is_assessment) {
                    navigate(`/labs/${lab_id}/nodes`)
                } else {
                    navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
                }
            })
            .catch((err) => {
                let status = err.response.status;
                console.log('error', status);
                navigate('/oops');
            });
    };

    const resetWalkthrough = (data) => {
        if (data.status === 'finished' | data.action === 'skip' | data.action === 'tour:end') {
            setIsMuted(!isMuted)
        }
    }
    useEffect(() => {
        const resourceIncluded = context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id);
        if (resourceIncluded && !startLab) {
            setIsMuted(!isMuted)

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.user?.stop_walkthrough])

    const showConfirm = () => {
        setShowFinishConfirm(true);
    }

    const handleConfirmClick = () => {
        handleNext();
    }

    const handleCopyButton = async () => {
        try {
            await navigator.clipboard.writeText(steps[currentStep]?.command);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
            toast.dark(props.t('Labs.Nodes.NodeAutomation.toast.warn'), {
                type: 'warning',
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        } catch (error) {
            toast.dark(props.t('Labs.Nodes.NodeAutomation.copyCode.alert'), {
                type: 'error',
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    }

    const handleCopy = async (e) => {
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
        toast.dark(props.t('Labs.Nodes.NodeAutomation.toast.warn'), {
            type: 'warning',
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    };

    const handleCopyDisabled = (e) => {
        e.preventDefault();
    }

    return (
        <>
            {loading ? (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : steps ? (
                <>
                    <BackConfirmationCard
                        isSaveAvailable={true}
                    />
                    <div
                        id="ancestorContainer"
                        className={Styles.ancestorContainer}
                        onKeyDown={(ev) => {
                            if (isCorrect && ev.key === 'Enter') {
                                ev.preventDefault();
                            }
                        }}
                    >
                        <ConfirmationCard
                            title={nodeInfo.is_assessment ?
                                props.t("Labs.Nodes.NodeParser.confirm.start.assessment.title")
                                : props.t("Labs.Nodes.NodeParser.confirm.start.default.title")
                            }
                            body={nodeInfo.is_assessment ?
                                props.t("Labs.Nodes.NodeParser.confirm.start.assessment.body")
                                : props.t("Labs.Nodes.NodeParser.confirm.start.default.body")
                            }
                            showCard={startLab}
                            setShowCard={setStartLab}
                            handleCancel={navigateBack}
                            handleConfirm={startLabClick}
                        />
                        {nodeInfo.is_assessment === 0 ? (
                            <Joyride
                                open={nodeInfo?.resource?.walkthrough?.open}
                                run={nodeInfo?.resource?.walkthrough?.run}
                                steps={nodeInfo?.resource?.walkthrough?.steps || []}
                                resourceId={nodeInfo?.resource?.lab_resource_id}
                                disableOverlay={true}
                                callback={resetWalkthrough}
                            />
                        ) : null}

                        <ToastContainer
                            id="warning-toast"
                            position="bottom-right"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                            limit={2}
                        />
                        <div id="titleContainer" className={Styles.titleContainer}>
                            <div style={{ display: 'flex' }}>

                                <Avatar
                                    sx={{
                                        bgcolor: pink[500],
                                        alignSelf: 'center',
                                        mr: 1,
                                        width: 55,
                                        height: 55,
                                    }}
                                >
                                    <ManageSearchIcon sx={{ width: 44, height: 44 }} />
                                </Avatar>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        textTransform: 'uppercase',
                                        color: '#FFFFFF',
                                    }}
                                >
                                    {nodeInfo.name}
                                </Typography>
                            </div>
                            <span style={{ display: 'flex', flexDirection: 'row' }}>
                                <LightTooltip
                                    title={nodeInfo.is_assessment
                                        ? ((roundedPercentage > 70) || nodeFinished ?
                                            props.t('Labs.Nodes.NodeParser.tooltip.finish.done')
                                            : props.t('Labs.Nodes.NodeParser.tooltip.finish.assessment')
                                        )
                                        : (nodeFinished ?
                                            props.t('Labs.Nodes.NodeParser.tooltip.finish.done')
                                            : props.t('Labs.Nodes.NodeParser.tooltip.finish.info')
                                        )
                                    }
                                    arrow
                                    placement="left"
                                    classes={{
                                        tooltip: {
                                            backgroundColor: '#fff',
                                            color: 'rgba(0, 0, 0, 0.87)',
                                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                                            fontSize: 11,
                                        },
                                    }}
                                >
                                    <span>
                                        <button
                                            variant="extended"
                                            className={Styles.finishButton}
                                            onClick={() => {
                                                nodeInfo.is_assessment ? showConfirm() : handleNext()
                                            }}
                                            disabled={
                                                !nodeInfo.is_assessment
                                                    ? !nodeFinished
                                                    : roundedPercentage < 70
                                            }
                                        >
                                            <KeyboardDoubleArrowRightIcon />
                                            {props.t('Labs.Nodes.NodeParser.button')}
                                        </button>
                                    </span>
                                </LightTooltip>
                                <ConfirmationCard
                                    title={(roundedPercentage > 70 && roundedPercentage < 100) ?
                                        props.t('Labs.Nodes.NodeParser.confirm.completed.title.warn')
                                        : props.t('Labs.Nodes.NodeParser.confirm.completed.title.default')
                                    }
                                    body={(roundedPercentage > 70 && roundedPercentage < 100) ?
                                        props.t('Labs.Nodes.NodeParser.confirm.completed.body.warn')
                                        : props.t('Labs.Nodes.NodeParser.confirm.completed.body.default')
                                    }
                                    showCard={showFinishConfirm}
                                    setShowCard={setShowFinishConfirm}
                                    handleConfirm={handleConfirmClick}
                                />
                                <NodeProgress
                                    lab_id={lab_id}
                                    node_id={node_id}
                                    currentStep={currentStep}
                                    setDrawerOpen={setDrawerOpen}
                                    drawerOpen={drawerOpen}
                                    setCurrentStep={setCurrentStep}
                                    initialUserProgress={nodeInfo.userProgress}
                                    steps={steps}
                                    data={data}
                                    setMaxUnlocked={setMaxUnlocked}
                                    maxUnlocked={maxUnlocked}
                                    resource={nodeInfo?.resource}
                                    onReveal={() => setShowReveal(true)}
                                    nodeInfo={nodeInfo}
                                />
                            </span>

                        </div>

                        <div id="midContainer" className={Styles.midContainer}>
                            <div id="regexContainer" className={Styles.regexContainer}>
                                <NodeParserEditor
                                    parser={nodeInfo?.resource?.resource_name}
                                    matchedText={highlightText}
                                    textToDisplay={
                                        nodeInfo?.resource?.resource_name === 'json'
                                            ? JSON.stringify(JSON.parse(steps[currentStep].text), null, 4)
                                            : nodeInfo?.resource?.resource_name === 'xml'
                                                ? nodeInfo.steps.text
                                                : steps[currentStep].text
                                    }
                                    onInputChange={handleUserInputChange}
                                    handleClick={() => handleMatchClick(nodeInfo?.resource?.resource_name)}
                                    moveNext={moveToNext}
                                    movePrev={moveToPrev}
                                    isCorrect={isCorrect}
                                    shouldDisplay={nodeFinished}
                                    isDisabled={currentStep === maxUnlocked}
                                    currentHeading={steps[currentStep].timeline}
                                    userInput={userInput}
                                />
                            </div>
                            <div data-testid="nodeParser-midContainer-instructionsContainer" id="instructionsContainer" className={Styles.instructionsContainer}>
                                <InstructionPane
                                    props={props}
                                    steps={steps}
                                    currentStep={currentStep}
                                    isMuted={isMuted}
                                    copied={copied}
                                    setIsMuted={setIsMuted}
                                    isCopyEnabled={isCopyEnabled}
                                    onHandleCopy={handleCopy}
                                    onHandleVoiceClick={handleVoiceClick}
                                    onHandleCopyDisabled={handleCopyDisabled}
                                    onHandleCopyButton={handleCopyButton}
                                    nodeInfo={nodeInfo}
                                    showLineNumbers={false}
                                    theme="light"
                                />
                            </div>
                            <div data-testid="nodeParser-midContainer-timeline" id="timeline" className={Styles.timeline}>
                                <NodeTimeline
                                    hideToolTip={false}
                                    collapsable={true}
                                    nodeFinished={nodeFinished}
                                    currentStep={currentStep}
                                    steps={steps}
                                    currentRef={currentRef}
                                    setCurrentStep={setCurrentStep}
                                    maxUnlocked={maxUnlocked}
                                    onStepChange={(index) => {
                                        setUserInput(steps[index]?.userInput || "");
                                        setHighlightText('');
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <InfoDrawer
                        handleDrawer={handleDrawer}
                        drawerOpen={drawerOpen}
                        titleText={props.t('Labs.Nodes.NodeParser.reference', { LabName: nodeInfo.labName })}
                        displayData={displayData}
                    />
                    {(!startLab && !nodeFinished && nodeInfo.is_assessment !== 1 && nodeInfo?.resource?.config?.chatbotTopic) && (
                        <HelperChat
                            active={props.aiChat}
                            topic={nodeInfo?.resource?.config?.chatbotTopic}
                        />
                    )}
                    {nodeFinished && <Feedback isInNode={true} nodeName={nodeInfo.name} labName={nodeInfo.labName} />}

                    <RevealAnswerKey
                        showAnswerKeyDialog={showReveal}
                        setShowAnswerKeyDialog={setShowReveal}
                        steps={steps}
                        currentStep={currentStep}
                        language={"plaintext"}
                        nodeId={node_id}
                        nodeName={nodeInfo?.name}
                        labTitle={nodeInfo?.labName}
                    />
                </>
            ) : (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            )}
        </>
    );
}
